/**
 * OfflineContentService - Main coordinator for offline reading
 *
 * Retrieves and decrypts offline content for reading.
 * Coordinates between database, encryption, and reader components.
 *
 * Features:
 * - Retrieve encrypted content from IndexedDB
 * - Decrypt content in memory
 * - Provide content to reader components
 * - Track reading state
 * - Check offline availability
 */

import { offlineDatabase } from './OfflineDatabase';
import { encryptionService } from './EncryptionService';
import { storageManager } from './StorageManager';
import { drmQuotaService } from './DrmQuotaService';
import { resourceExtractor } from './ResourceExtractor';
import type {
  OfflineBook,
  OfflineBookContent,
  ChapterContent,
  OfflineReadingState,
  OfflineLicense,
  DownloadedChapterInfo,
  ChapterSelectionInfo,
  LicenseValidationResult,
} from '@/types/offline';
import type { BookMetadataDto } from '@/types/book';
import { LicenseType } from '@/types/library';

export class OfflineContentService {
  /**
   * Check if book is available offline
   */
  async isBookAvailableOffline(bookId: string): Promise<boolean> {
    return offlineDatabase.isBookOffline(bookId);
  }

  /**
   * Get offline book metadata
   */
  async getOfflineBook(bookId: string): Promise<OfflineBook | undefined> {
    return offlineDatabase.getBook(bookId);
  }

  /**
   * Get all offline books
   */
  async getAllOfflineBooks(): Promise<OfflineBook[]> {
    return offlineDatabase.getAllBooks();
  }

  /**
   * Get EPUB chapter content (decrypted)
   */
  async getEpubChapter(
    bookId: string,
    chapterIndex: number,
    userId: string
  ): Promise<ChapterContent | null> {
    try {
      console.log(`📚 Looking for chapter ${chapterIndex} of book ${bookId} for user ${userId}`);

      // Get encrypted chunk
      const chunk = await offlineDatabase.getEpubChunk(bookId, chapterIndex);

      if (!chunk) {
        // Debug: Check all chunks for this book
        const allChunks = await offlineDatabase.getEpubChunks(bookId);
        console.error(`❌ Chapter ${chapterIndex} not found for book ${bookId}`);
        console.error(`   Available chapters: ${allChunks.map(c => c.chapterIndex).join(', ')}`);
        console.error(`   Total chunks in DB: ${allChunks.length}`);
        return null;
      }

      console.log(`✅ Found encrypted chunk for chapter ${chapterIndex}, size: ${chunk.encryptedContent.byteLength} bytes`);

      // Get book metadata for key derivation
      const book = await offlineDatabase.getBook(bookId);

      if (!book) {
        console.error(`Book ${bookId} not found in offline storage`);
        return null;
      }

      // Derive decryption key
      const masterKey = await encryptionService.deriveMasterKey(userId, userId);
      const bookKey = await encryptionService.deriveBookKey(masterKey, bookId);

      // Decrypt content
      const decryptedBytes = await encryptionService.decrypt(
        {
          data: chunk.encryptedContent,
          iv: chunk.iv,
        },
        bookKey.key
      );

      // Convert to string
      const decoder = new TextDecoder();
      let htmlContent = decoder.decode(decryptedBytes);

      // Load and inject chapter resources (images, CSS, fonts)
      htmlContent = await this.injectChapterResources(
        bookId,
        chapterIndex,
        htmlContent,
        bookKey.key
      );

      // Update last accessed
      await offlineDatabase.updateLastAccessed(bookId);

      return {
        chapterIndex,
        title: chunk.chapterTitle,
        htmlContent,
      };
    } catch (error) {
      console.error('Failed to retrieve EPUB chapter:', error);
      return null;
    }
  }

  /**
   * Load chapter resources and inject blob URLs into HTML
   */
  private async injectChapterResources(
    bookId: string,
    chapterIndex: number,
    htmlContent: string,
    bookKey: CryptoKey
  ): Promise<string> {
    try {
      // Get chapter resources from database
      const resources = await offlineDatabase.getChapterResources(bookId, chapterIndex);

      if (resources.length === 0) {
        console.log(`📦 No resources to inject for chapter ${chapterIndex}`);
        return htmlContent;
      }

      console.log(`📦 Injecting ${resources.length} resources into chapter ${chapterIndex}...`);

      // Create blob URLs for each resource
      const urlMap = new Map<string, string>();

      for (const resource of resources) {
        try {
          // Decrypt resource
          const decryptedBytes = await encryptionService.decrypt(
            {
              data: resource.encryptedContent,
              iv: resource.iv,
            },
            bookKey
          );

          console.log(`  🔓 Decrypted ${resource.resourceType}: ${resource.originalUrl.substring(0, 80)}`);
          console.log(`     - Encrypted size: ${resource.encryptedContent.byteLength} bytes`);
          console.log(`     - Decrypted size: ${decryptedBytes.byteLength} bytes`);
          console.log(`     - MIME type: ${resource.mimeType}`);

          // Create blob URL
          const blob = new Blob([decryptedBytes], { type: resource.mimeType });
          const blobUrl = URL.createObjectURL(blob);

          console.log(`     - Blob size: ${blob.size} bytes`);
          console.log(`     - Blob URL: ${blobUrl}`);

          urlMap.set(resource.originalUrl, blobUrl);
          console.log(`     ✅ Injected successfully\n`);
        } catch (err) {
          console.error(`Failed to decrypt resource ${resource.originalUrl}:`, err);
          // Continue with other resources
        }
      }

      // Replace URLs in HTML
      const modifiedHtml = resourceExtractor.replaceResourceUrls(htmlContent, urlMap);

      console.log(`💉 Injected ${urlMap.size} blob URLs into chapter ${chapterIndex}`);
      return modifiedHtml;
    } catch (error) {
      console.error(`Failed to inject chapter resources for chapter ${chapterIndex}:`, error);
      // Return original HTML if injection fails
      return htmlContent;
    }
  }

  /**
   * Get all EPUB chapters (decrypted) - ONLY returns downloaded chapters
   * For partial downloads, this returns only the chapters the user has downloaded
   */
  async getAllEpubChapters(
    bookId: string,
    userId: string
  ): Promise<ChapterContent[]> {
    const book = await offlineDatabase.getBook(bookId);

    if (!book || book.format !== 'EPUB') {
      return [];
    }

    // Get only downloaded chapters
    const downloadedChapters = await this.getDownloadedChapters(bookId);
    const chapters: ChapterContent[] = [];

    for (const chapterInfo of downloadedChapters) {
      const chapter = await this.getEpubChapter(bookId, chapterInfo.chapterIndex, userId);
      if (chapter) {
        chapters.push(chapter);
      }
    }

    return chapters;
  }

  /**
   * Get PDF content (decrypted as ArrayBuffer)
   */
  async getPdfContent(bookId: string, userId: string): Promise<ArrayBuffer | null> {
    try {
      // Get encrypted PDF page (full document)
      const pages = await offlineDatabase.getPdfPages(bookId);

      if (pages.length === 0) {
        console.error(`PDF not found for book ${bookId}`);
        return null;
      }

      const pdfPage = pages[0]; // Full document is stored as single page

      // Get book metadata for key derivation
      const book = await offlineDatabase.getBook(bookId);

      if (!book) {
        console.error(`Book ${bookId} not found in offline storage`);
        return null;
      }

      // Derive decryption key
      const masterKey = await encryptionService.deriveMasterKey(userId, userId);
      const bookKey = await encryptionService.deriveBookKey(masterKey, bookId);

      // Decrypt PDF
      const decryptedPdf = await encryptionService.decrypt(
        {
          data: pdfPage.encryptedContent,
          iv: pdfPage.iv,
        },
        bookKey.key
      );

      // Update last accessed
      await offlineDatabase.updateLastAccessed(bookId);

      return decryptedPdf;
    } catch (error) {
      console.error('Failed to retrieve PDF:', error);
      return null;
    }
  }

  /**
   * Get full offline book content (for reader initialization)
   */
  async getOfflineBookContent(
    bookId: string,
    userId: string
  ): Promise<OfflineBookContent | null> {
    const book = await offlineDatabase.getBook(bookId);

    if (!book) {
      return null;
    }

    if (book.format === 'EPUB') {
      const chapters = await this.getAllEpubChapters(bookId, userId);
      return {
        bookId,
        format: 'EPUB',
        metadata: book,
        chapters,
      };
    } else {
      const pdfData = await this.getPdfContent(bookId, userId);
      if (!pdfData) {
        return null;
      }

      return {
        bookId,
        format: 'PDF',
        metadata: book,
        pdfData,
      };
    }
  }

  /**
   * Get reading state for a book
   */
  async getReadingState(bookId: string): Promise<OfflineReadingState | undefined> {
    return offlineDatabase.getReadingState(bookId);
  }

  /**
   * Update reading state (offline - will sync later)
   */
  async updateReadingState(state: OfflineReadingState): Promise<void> {
    await offlineDatabase.updateReadingState(state);
  }

  /**
   * Update reading progress
   */
  async updateProgress(
    bookId: string,
    position: number | string,
    progressPercentage: number
  ): Promise<void> {
    const existingState = await offlineDatabase.getReadingState(bookId);

    const newState: OfflineReadingState = {
      bookId,
      currentPosition: position,
      progressPercentage,
      lastRead: new Date(),
      highlights: existingState?.highlights || [],
      notes: existingState?.notes || [],
      bookmarks: existingState?.bookmarks || [],
    };

    await offlineDatabase.updateReadingState(newState);
  }

  /**
   * Check if user has valid license for offline book
   */
  async hasValidLicense(bookId: string, userId: string): Promise<LicenseValidationResult> {
    const license = await offlineDatabase.getLicense(bookId, userId);

    if (!license) {
      return { valid: false, reason: 'no-license' };
    }

    if (license.licenseType === LicenseType.Purchase) {
      return { valid: true };
    }

    if (!license.expirationDate) {
      return { valid: true };
    }

    const now = new Date();
    const expiry = license.expirationDate instanceof Date
      ? license.expirationDate
      : new Date(license.expirationDate);

    if (expiry > now) {
      return { valid: true };
    }

    await offlineDatabase.deleteBook(bookId);

    return {
      valid: false,
      reason: 'expired',
      expirationDate: expiry,
    };
  }

  /**
   * Get license info
   */
  async getLicense(bookId: string, userId: string): Promise<OfflineLicense | undefined> {
    return offlineDatabase.getLicense(bookId, userId);
  }

  /**
   * Delete offline book
   */
  async deleteOfflineBook(bookId: string): Promise<void> {
    await offlineDatabase.deleteBook(bookId);
    await storageManager.deleteBook(bookId);
  }

  /**
   * Get storage statistics
   */
  async getStorageStats() {
    return storageManager.getStorageStats();
  }

  /**
   * Get EPUB table of contents (chapter list with titles)
   */
  async getEpubTOC(bookId: string): Promise<Array<{ index: number; title: string }>> {
    const chunks = await offlineDatabase.getEpubChunks(bookId);
    return chunks.map(chunk => ({
      index: chunk.chapterIndex,
      title: chunk.chapterTitle,
    }));
  }

  /**
   * Clear all offline content (for logout or reset)
   */
  async clearAllOfflineContent(): Promise<void> {
    await offlineDatabase.clearAllData();
    encryptionService.clearKeyCache();
    console.log('🧹 All offline content cleared');
  }

  /**
   * Get database stats
   */
  async getDatabaseStats() {
    return offlineDatabase.getStats();
  }

  // ============================================
  // Highlights, Notes & Bookmarks Offline Support
  // ============================================

  /**
   * Sync highlights from API to offline storage
   * Called during book download to cache highlights for offline viewing
   */
  async syncHighlightsToOffline(bookId: string, highlights: HighlightData[]): Promise<void> {
    const existingState = await offlineDatabase.getReadingState(bookId);

    const newState: OfflineReadingState = {
      bookId,
      currentPosition: existingState?.currentPosition || 0,
      progressPercentage: existingState?.progressPercentage || 0,
      lastRead: existingState?.lastRead || new Date(),
      highlights: highlights,
      notes: existingState?.notes || [],
      bookmarks: existingState?.bookmarks || [],
    };

    await offlineDatabase.updateReadingState(newState);
    console.log(`💾 Synced ${highlights.length} highlights to offline storage for book ${bookId}`);
  }

  /**
   * Sync notes from API to offline storage
   * Called during book download to cache notes for offline viewing
   */
  async syncNotesToOffline(bookId: string, notes: NoteData[]): Promise<void> {
    const existingState = await offlineDatabase.getReadingState(bookId);

    const newState: OfflineReadingState = {
      bookId,
      currentPosition: existingState?.currentPosition || 0,
      progressPercentage: existingState?.progressPercentage || 0,
      lastRead: existingState?.lastRead || new Date(),
      highlights: existingState?.highlights || [],
      notes: notes,
      bookmarks: existingState?.bookmarks || [],
    };

    await offlineDatabase.updateReadingState(newState);
    console.log(`💾 Synced ${notes.length} notes to offline storage for book ${bookId}`);
  }

  /**
   * Sync bookmarks from API to offline storage
   * Called during book download to cache bookmarks for offline viewing
   */
  async syncBookmarksToOffline(bookId: string, bookmarks: BookmarkData[]): Promise<void> {
    const existingState = await offlineDatabase.getReadingState(bookId);

    const newState: OfflineReadingState = {
      bookId,
      currentPosition: existingState?.currentPosition || 0,
      progressPercentage: existingState?.progressPercentage || 0,
      lastRead: existingState?.lastRead || new Date(),
      highlights: existingState?.highlights || [],
      notes: existingState?.notes || [],
      bookmarks: bookmarks,
    };

    await offlineDatabase.updateReadingState(newState);
    console.log(`💾 Synced ${bookmarks.length} bookmarks to offline storage for book ${bookId}`);
  }

  /**
   * Sync all annotations (highlights, notes, bookmarks) in one operation
   * More efficient than calling each sync method separately
   */
  async syncAllAnnotationsToOffline(
    bookId: string,
    highlights: HighlightData[],
    notes: NoteData[],
    bookmarks: BookmarkData[]
  ): Promise<void> {
    const existingState = await offlineDatabase.getReadingState(bookId);

    const newState: OfflineReadingState = {
      bookId,
      currentPosition: existingState?.currentPosition || 0,
      progressPercentage: existingState?.progressPercentage || 0,
      lastRead: existingState?.lastRead || new Date(),
      highlights,
      notes,
      bookmarks,
    };

    await offlineDatabase.updateReadingState(newState);
    console.log(`💾 Synced ${highlights.length} highlights, ${notes.length} notes, ${bookmarks.length} bookmarks to offline storage for book ${bookId}`);
  }

  /**
   * Get offline highlights for a book
   * Returns empty array if not available offline
   */
  async getOfflineHighlights(bookId: string): Promise<HighlightData[]> {
    const state = await offlineDatabase.getReadingState(bookId);
    return state?.highlights || [];
  }

  /**
   * Get offline notes for a book
   * Returns empty array if not available offline
   */
  async getOfflineNotes(bookId: string): Promise<NoteData[]> {
    const state = await offlineDatabase.getReadingState(bookId);
    return state?.notes || [];
  }

  /**
   * Get offline bookmarks for a book
   * Returns empty array if not available offline
   */
  async getOfflineBookmarks(bookId: string): Promise<BookmarkData[]> {
    const state = await offlineDatabase.getReadingState(bookId);
    return state?.bookmarks || [];
  }

  // ============================================
  // DRM-Aware Partial Download Support
  // ============================================

  /**
   * Get list of downloaded chapters for a book
   */
  async getDownloadedChapters(bookId: string): Promise<DownloadedChapterInfo[]> {
    return offlineDatabase.getDownloadedChapters(bookId);
  }

  /**
   * Check if specific chapter is downloaded
   */
  async isChapterDownloaded(bookId: string, chapterIndex: number): Promise<boolean> {
    return offlineDatabase.isChapterDownloaded(bookId, chapterIndex);
  }

  /**
   * Get downloadable chapter count based on DRM quota
   */
  async getDownloadableChapterCount(userId: string, bookId: string): Promise<number> {
    return drmQuotaService.getDownloadableChapterCount(userId, bookId);
  }

  /**
   * Get detailed info about which chapters can be downloaded
   * (considers both download status and DRM quota)
   */
  async getDownloadableChaptersInfo(
    bookId: string,
    userId: string,
    bookMetadata: BookMetadataDto
  ): Promise<ChapterSelectionInfo[]> {
    // Get downloaded chapters
    const downloadedChapters = await this.getDownloadedChapters(bookId);
    const downloadedIndices = new Set(downloadedChapters.map(c => c.chapterIndex));
    const downloadedMap = new Map(downloadedChapters.map(c => [c.chapterIndex, c]));

    // Get remaining quota
    let remainingQuota = 0;
    let quotaExceeded = false;

    if (bookMetadata.isDrmEnabled) {
      try {
        const quotaInfo = await drmQuotaService.getQuota(userId, bookId);
        remainingQuota = quotaInfo.remainingQuota;
        quotaExceeded = remainingQuota === 0;
      } catch (error) {
        console.error('Failed to get DRM quota:', error);
        quotaExceeded = true;
      }
    } else {
      // No DRM - unlimited downloads
      remainingQuota = 999999;
    }

    // Build chapter selection info
    const chapterSelectionInfo: ChapterSelectionInfo[] = bookMetadata.chapters.map((chapter, index) => {
      const isDownloaded = downloadedIndices.has(index);
      const downloadInfo = downloadedMap.get(index);

      return {
        chapterIndex: index,
        title: chapter.title,
        isDownloaded,
        downloadedAt: downloadInfo?.downloadedAt,
        canDownload: isDownloaded ? false : remainingQuota > 0, // Already downloaded = can't download again
        quotaExceeded,
      };
    });

    return chapterSelectionInfo;
  }

  /**
   * Get count of downloaded chapters for a book
   */
  async getDownloadedChapterCount(bookId: string): Promise<number> {
    return offlineDatabase.getDownloadedChapterCount(bookId);
  }

  /**
   * Check if book has any downloaded chapters
   */
  async hasAnyDownloadedChapters(bookId: string): Promise<boolean> {
    const count = await this.getDownloadedChapterCount(bookId);
    return count > 0;
  }

  /**
   * Get EPUB table of contents with download status
   * Shows which chapters are available offline
   */
  async getEpubTOCWithStatus(
    bookId: string
  ): Promise<Array<{ index: number; title: string; isDownloaded: boolean; downloadedAt?: Date }>> {
    const downloadedChapters = await offlineDatabase.getDownloadedChapters(bookId);
    const downloadedMap = new Map(downloadedChapters.map(c => [c.chapterIndex, c]));

    return downloadedChapters.map(chapter => ({
      index: chapter.chapterIndex,
      title: '', // We don't store title in DownloadedChapterInfo, need to get from chunks
      isDownloaded: true,
      downloadedAt: chapter.downloadedAt,
    }));
  }
}

// Singleton instance
export const offlineContentService = new OfflineContentService();
